import axiosInstance from "@/setup/axios/axiosInstance";
import { getSelectedCompany } from "@/modules/auth/helpers/CompanyStorage";
// Get order history
export const getOrderHistory = async (orderId: string, filters?: { page?: number; page_size?: number }) => {
  try {
    const params = new URLSearchParams();

    if (filters?.page) {
      params.append("page", String(filters.page));
    }

    if (filters?.page_size) {
      params.append("page_size", String(filters.page_size));
    }

    const queryString = params.toString();
    const response = await axiosInstance.get(
      queryString ? `/orders/${orderId}/history/?${queryString}` : `/orders/${orderId}/history/`
    );
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

// Get orders
export const getOrders = async (filters?: any) => {
  try {
    const params = new URLSearchParams();
    const selectedCompany = getSelectedCompany();
    const selectedCompanyId = selectedCompany?.company_id;

    if (filters) {
      if (filters.page) {
        params.append("page", String(filters.page));
      }
      if (filters.page_size) {
        params.append("page_size", String(filters.page_size));
      }

      // Search query
      if (filters.search) {
        params.append("search", filters.search);
      }
      
      // Date filters
      if (filters.dateFrom) {
        params.append("sale_date__gte", filters.dateFrom);
      }
      if (filters.dateTo) {
        // Nếu có flag dateToUseLt, dùng __lt (less than) thay vì __lte (less than or equal)
        // Điều này đảm bảo filter chính xác hơn, tránh timezone issues
        if (filters.dateToUseLt) {
          params.append("sale_date__lt", filters.dateTo);
        } else {
          // Dùng __lte với datetime để bao gồm cả ngày cuối cùng
          params.append("sale_date__lte", filters.dateTo);
        }
      }

      // Reward date filters
      if (filters.rewardDateFrom) {
        params.append("reward_date__gte", filters.rewardDateFrom);
      }
      if (filters.rewardDateTo) {
        if (filters.rewardDateToUseLt) {
          params.append("reward_date__lt", filters.rewardDateTo);
        } else {
          params.append("reward_date__lte", filters.rewardDateTo);
        }
      }
      
      // Creation date filters
      if (filters.creationDateFrom) {
        params.append("created_at__gte", filters.creationDateFrom);
      }
      if (filters.creationDateTo) {
        params.append("created_at__lte", filters.creationDateTo);
      }
      
      // Status filters
      if (filters.status) {
        params.append("status", filters.status);
      }
      if (filters.paymentStatus) {
        params.append("payment_status", filters.paymentStatus);
      }
      
      // Customer and employee filters
      if (filters.customer) {
        params.append("customer", filters.customer);
      }
      if (filters.employee) {
        // Backend uses 'sales_employee' as the filter field name
        params.append("sales_employee", filters.employee);
      }
      if (filters.survey) {
        params.append("survey__survey_employee", filters.survey);
      }
      if (filters.surveyDateFrom) {
        params.append("survey__survey_date__gte", filters.surveyDateFrom);
      }
      if (filters.surveyDateTo) {
        if (filters.surveyDateToUseLt) {
          params.append("survey__survey_date__lt", filters.surveyDateTo);
        } else {
          params.append("survey__survey_date__lte", filters.surveyDateTo);
        }
      }
      if (filters.tech_cnc) {
        params.append("tech_cnc__tech_cnc_employee", filters.tech_cnc);
      }
      if (filters.techCncExportFileDateFrom) {
        params.append("tech_cnc__export_file_date__gte", filters.techCncExportFileDateFrom);
      }
      if (filters.techCncExportFileDateTo) {
        if (filters.techCncExportFileDateToUseLt) {
          params.append("tech_cnc__export_file_date__lt", filters.techCncExportFileDateTo);
        } else {
          params.append("tech_cnc__export_file_date__lte", filters.techCncExportFileDateTo);
        }
      }
      if (filters.supplier) {
        params.append("supplier", filters.supplier);
      }
      if (filters.company) {
        params.append("company", filters.company);
        // Also try company_id in case backend expects that
        params.append("company_id", filters.company);
      }
      
      // Sales channel filter
      if (filters.salesChannel) {
        params.append("sales_channel", filters.salesChannel);
      }
      
      // Order code filter
      if (filters.orderCode) {
        params.append("code", filters.orderCode);
      }
      
      // Amount filters
      if (filters.amountFrom) {
        params.append("total_amount__gte", filters.amountFrom);
      }
      if (filters.amountTo) {
        params.append("total_amount__lte", filters.amountTo);
      }
      
      // Notes filter
      if (filters.notes) {
        params.append("notes__icontains", filters.notes);
      }
      
      // Priority filter
      if (filters.priority && filters.priority !== 'all') {
        params.append("priority", filters.priority);
      }
      
      // Updater name filter
      if (filters.updaterName) {
        params.append("updated_by__name__icontains", filters.updaterName);
      }
      
      // Legacy filters for backward compatibility
      if (filters.status && Array.isArray(filters.status) && filters.status.length > 0) {
        filters.status.forEach((status: string) =>
          params.append("status", status)
        );
      }
      if (filters.customer_id) {
        params.append("customer", filters.customer_id);
      }
      if (filters.date_from) {
        params.append("sale_date__gte", filters.date_from);
      }
      if (filters.date_to) {
        params.append("sale_date__lte", filters.date_to);
      }
    }

    const hasCompanyFilter = params.has("company") || params.has("company_id");
    if (!hasCompanyFilter && selectedCompanyId && selectedCompanyId !== "global") {
      params.append("company", selectedCompanyId);
    }

    const url = `/orders/?${params.toString()}`;
    const response = await axiosInstance.get(url);
    return response;
  } catch (error) {
    throw error;
  }
};

// Get order by ID
export const getOrderById = async (id: string) => {
  try {
    const response = await axiosInstance.get(`/orders/${id}/`);
    return response;
  } catch (error) {
    throw error;
  }
};

// Create order
export const createOrder = async (orderData: any) => {
  try {
    const response = await axiosInstance.post("/orders/", orderData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Update order
export const updateOrder = async (id: string, orderData: any) => {
  try {
    const response = await axiosInstance.put(`/orders/${id}/`, orderData);

    return response.data;
  } catch (error) {
    throw error;
  }
};

// Update order dates only (uses PATCH for partial update)
export const updateOrderDates = async (id: string, dates: { completion_date?: string | null; reward_date?: string | null }) => {
  try {
    const response = await axiosInstance.patch(`/orders/${id}/`, dates);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Delete order
export const deleteOrder = async (id: string) => {
  try {
    const response = await axiosInstance.delete(`/orders/${id}/`);
    return response;
  } catch (error) {
    throw error;
  }
};

// Get order statuses by category
export const getOrderStatusesByCategory = async (categoryId: string) => {
  try {
    const response = await axiosInstance.get(`/orders/order-statuses/?category=${categoryId}`);
    const statuses = response.data.results || response.data;
    return statuses;
  } catch (error) {
    throw error;
  }
};

// Get single order status
export const getOrderStatus = async (id: string) => {
  try {
    const response = await axiosInstance.get(`/orders/order-statuses/${id}/`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Create order status
export const createOrderStatus = async (statusData: {
  name: string;
  category_id: string;
  code?: string;
  description?: string;
  color?: string;
  sort_order?: number;
  is_final?: boolean;
}) => {
  try {
    const response = await axiosInstance.post(
      "/orders/order-statuses/",
      statusData
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Update order status
export const updateOrderStatus = async (
  id: string,
  statusData: {
    name?: string;
    category_id?: string;
    code?: string;
    description?: string;
    color?: string;
    sort_order?: number;
    is_final?: boolean;
  }
) => {
  try {
    const response = await axiosInstance.patch(
      `/orders/order-statuses/${id}/`,
      statusData
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Delete order status
export const deleteOrderStatus = async (id: string) => {
  try {
    await axiosInstance.delete(`/orders/order-statuses/${id}/`);
    return true;
  } catch (error) {
    throw error;
  }
};

// Update order status color
export const updateOrderStatusColor = async (statusId: string, color: string) => {
  try {
    const response = await axiosInstance.patch(
      `/orders/order-statuses/${statusId}/`,
      { color }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Batch update order status colors
export const batchUpdateOrderStatusColors = async (colors: Record<string, string>) => {
  try {
    const updates = Object.entries(colors).map(([statusId, color]) =>
      axiosInstance.patch(`/orders/order-statuses/${statusId}/`, { color })
    );
    const responses = await Promise.all(updates);
    return responses.map(r => r.data);
  } catch (error) {
    throw error;
  }
};

// Get all order status categories
export const getOrderStatusCategories = async () => {
  try {
    const response = await axiosInstance.get("/orders/order-status-categories/");
    const categories = response.data.results || response.data;
    return categories;
  } catch (error) {
    throw error;
  }
};

// Get single order status category
export const getOrderStatusCategory = async (id: string) => {
  try {
    const response = await axiosInstance.get(`/orders/order-status-categories/${id}/`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Create new order status category
export const createOrderStatusCategory = async (categoryData: {
  name: string;
  code?: string;
  description?: string;
  color?: string;
  sort_order?: number;
}) => {
  try {
    const response = await axiosInstance.post(
      "/orders/order-status-categories/",
      categoryData
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Update order status category
export const updateOrderStatusCategory = async (
  id: string,
  categoryData: {
    name?: string;
    code?: string;
    description?: string;
    color?: string;
    sort_order?: number;
  }
) => {
  try {
    const response = await axiosInstance.patch(
      `/orders/order-status-categories/${id}/`,
      categoryData
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Delete order status category
export const deleteOrderStatusCategory = async (id: string) => {
  try {
    await axiosInstance.delete(`/orders/order-status-categories/${id}/`);
    return true;
  } catch (error) {
    throw error;
  }
};

// Create new sales channel
export const createSalesChannel = async (channelData: { name: string }) => {
  const response = await axiosInstance.post(
    "/orders/sales-channels/",
    channelData
  );
  return response;
};

// Create unit
export const createUnit = async (unitData: { name: string }) => {
  const response = await axiosInstance.post("/inventory/units/", unitData);
  return response;
};

// Create survey
export const createSurvey = async (surveyData: any) => {
  try {
    const response = await axiosInstance.post("/orders/surveys/", surveyData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Create tech CNC
export const createTechCnc = async (techCncData: any) => {
  try {
    const response = await axiosInstance.post(
      "/orders/tech-cncs/",
      techCncData
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Soft delete orders
export const softDeleteOrders = async (orderIds: string[]) => {
  try {
    const response = await axiosInstance.post("/orders/soft_delete/", {
      order_ids: orderIds,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Restore deleted orders
export const restoreOrders = async (orderIds: string[]) => {
  try {
    const response = await axiosInstance.post("/orders/restore_deleted/", {
      order_ids: orderIds,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Permanently delete orders
export const permanentDeleteOrders = async (orderIds: string[]) => {
  try {
    const response = await axiosInstance.post("/orders/permanent_delete/", {
      order_ids: orderIds,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Get deleted orders
export const getDeletedOrders = async () => {
  try {
    const selectedCompany = getSelectedCompany();
    const selectedCompanyId = selectedCompany?.company_id;

    const params: Record<string, string | boolean> = {
      show_deleted: true,
    };

    if (selectedCompanyId && selectedCompanyId !== "global") {
      params.company = selectedCompanyId;
    }

    const response = await axiosInstance.get("/orders/", { params });
    return response;
  } catch (error) {
    throw error;
  }
};

// Get order codes for autocomplete
export const getOrderCodes = async (search?: string, companyId?: string) => {
  try {
    const params = new URLSearchParams();
    if (search) {
      params.append("search", search);
    }

    const selectedCompany = getSelectedCompany();
    const selectedCompanyId = companyId ?? selectedCompany?.company_id;
    if (selectedCompanyId && selectedCompanyId !== 'global') {
      params.append('company', selectedCompanyId);
      params.append('company_id', selectedCompanyId);
    }
    // Limit to a reasonable number for autocomplete
    params.append("limit", "50");
    
    const url = `/orders/?${params.toString()}`;
    const response = await axiosInstance.get(url);
    
    // Extract unique order codes from the response
    const orders = response.data.results || response.data;
    const orderCodes = orders
      .map((order: any) => ({
        value: order.code,
        label: order.code,
      }))
      .filter((item: any, index: number, self: any[]) => 
        index === self.findIndex((t) => t.value === item.value)
      );
    
    return orderCodes;
  } catch (error) {
    throw error;
  }
};

// Get order statuses for filters
export const getOrderStatuses = async () => {
  try {
    const response = await axiosInstance.get("/settings/statuses/by-module/?module=orders");
    const payload = response.data?.orders || response.data;
    const statuses = Array.isArray(payload?.groups)
      ? payload.groups.flatMap((group: any) => {
          const groupStatus = group?.group_status || null;
          const items = Array.isArray(group?.items) ? group.items : [];
          return items.map((item: any) => ({
            ...item,
            group_status: item?.group_status || groupStatus,
          }));
        })
      : Array.isArray(payload?.items)
        ? payload.items
        : [];

    // Keep status ordering consistent with TopOrder: group sort, then item sort, then name.
    const statusOptions = statuses
      .map((status: any) => {
        const categorySource = status.group_status || status.category || null;
        const groupSortOrder = categorySource?.sort_order ?? 999;
        const itemSortOrder = status.sort_order ?? 0;

        return {
          value: status.id,
          label: status.name,
          code: status.code,
          categoryName: categorySource?.name || status.category_name || '',
          categoryCode: categorySource?.code || status.category_code || '',
          sortOrder: itemSortOrder,
          groupSortOrder,
          color: status.color || null,
        };
      })
      .sort((a: any, b: any) => {
        if (a.groupSortOrder !== b.groupSortOrder) return a.groupSortOrder - b.groupSortOrder;
        if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
        return (a.label || '').localeCompare(b.label || '', 'vi');
      });
    
    return statusOptions;
  } catch (error) {
    throw error;
  }
};

// Get customers for filters
export const getCustomers = async (search?: string, companyId?: string) => {
  try {
    const params = new URLSearchParams();
    if (search) {
      params.append("search", search);
    }

    const selectedCompany = getSelectedCompany();
    const selectedCompanyId = companyId ?? selectedCompany?.company_id;
    if (selectedCompanyId && selectedCompanyId !== 'global') {
      params.append('company', selectedCompanyId);
      params.append('company_id', selectedCompanyId);
    }
    // Limit to a reasonable number for autocomplete
    params.append("limit", "50");
    
    const url = `/customers/?${params.toString()}`;
    const response = await axiosInstance.get(url);
    
    // Extract customers from the response
    const customers = response.data.results || response.data;
    const customerOptions = customers.map((customer: any) => ({
      value: customer.id,
      label: `${customer.code} - ${customer.name}`,
      code: customer.code,
      name: customer.name,
      phone: customer.phone || '',
      email: customer.email || '',
      customerType: customer.customer_type || '',
    }));
    
    return customerOptions;
  } catch (error) {
    throw error;
  }
};

// Get employees for filters
export const getEmployees = async (search?: string, companyId?: string) => {
  try {
    const params = new URLSearchParams();
    if (search) {
      params.append("search", search);
    }
    // Add company filter if provided
    if (companyId) {
      params.append("company", companyId);
    }
    params.append('no_pagination', 'true');
    params.append('attendance_type', 'attendance');
    
    const url = `/employees/?${params.toString()}`;
    const response = await axiosInstance.get(url);
    
    // Extract employees from the response
    const employees = response.data.results || response.data;
    const employeeOptions = employees.map((employee: any) => {
      const displayName = employee.nickname || employee.name;
      return {
        value: employee.id,
        label: `${employee.code} - ${displayName}`,
        code: employee.code,
        name: displayName,
        status: employee.status || '',
        jobTitle: employee.job_title?.name || '',
        area: employee.area?.name || '',
        office: employee.office?.name || '',
        company_id: employee.company?.id || employee.company_id || null,
        company: employee.company || null,
      };
    });
    
    return employeeOptions;
  } catch (error) {
    throw error;
  }
};

// Get companies for filters
export const getCompanies = async (search?: string) => {
  try {
    const params = new URLSearchParams();
    if (search) {
      params.append("search", search);
    }
    
    const url = `/companies/?${params.toString()}`;
    const response = await axiosInstance.get(url);
    
    // Extract companies from the response
    const companies = response.data.results || response.data;
    const companyOptions = companies.map((company: any) => ({
      value: company.id,
      label: company.short_name || company.name,
      code: company.code,
      name: company.name,
      shortName: company.short_name || '',
      companyType: company.company_type || '',
    }));
    
    return companyOptions;
  } catch (error) {
    throw error;
  }
};

// Get sales statistics for a sales employee
export const getSalesStatistics = async (
  salesEmployeeId: string,
  period: 'month' | 'quarter' | 'year',
  year: number,
  monthOrQuarter?: number
) => {
  try {
    const params = new URLSearchParams();
    params.append("sales_employee", salesEmployeeId);
    
    // Calculate date range based on period
    let dateFrom: Date;
    let dateTo: Date;
    
    if (period === 'month' && monthOrQuarter) {
      // Month: from first day of selected month to last day of selected month
      dateFrom = new Date(year, monthOrQuarter - 1, 1);
      dateTo = new Date(year, monthOrQuarter, 0); // Last day of the month
    } else if (period === 'quarter' && monthOrQuarter) {
      // Quarter: from first day of first month in quarter to last day of last month in quarter
      const quarterStartMonth = (monthOrQuarter - 1) * 3;
      dateFrom = new Date(year, quarterStartMonth, 1);
      const quarterEndMonth = quarterStartMonth + 3;
      dateTo = new Date(year, quarterEndMonth, 0); // Last day of the last month in quarter
    } else { // year
      // Year: from first day of year to last day of year
      dateFrom = new Date(year, 0, 1);
      dateTo = new Date(year, 11, 31);
    }
    
    params.append("sale_date__gte", dateFrom.toISOString().split('T')[0]);
    params.append("sale_date__lte", dateTo.toISOString().split('T')[0]);
    
    const url = `/orders/?${params.toString()}`;
    const response = await axiosInstance.get(url);
    
    const orders = response.data.results || response.data;
    
    // Calculate statistics
    const totalOrders = Array.isArray(orders) ? orders.length : 0;
    const totalAmount = Array.isArray(orders) 
      ? orders.reduce((sum: number, order: any) => sum + (order.total_amount || 0), 0)
      : 0;
    
    // Count orders by status
    const ordersByStatus: Record<string, number> = {};
    let newOrders = 0;
    let completedOrders = 0;
    
    if (Array.isArray(orders)) {
      orders.forEach((order: any) => {
        const statusName = order.status?.name || order.status_name || 'Không xác định';
        ordersByStatus[statusName] = (ordersByStatus[statusName] || 0) + 1;
        
        // Count new orders (created in the selected period)
        if (order.created_at) {
          const createdDate = new Date(order.created_at);
          if (createdDate >= dateFrom && createdDate <= dateTo) {
            newOrders++;
          }
        }
        
        // Count completed orders (status "Kết thúc" or "Hoàn thành" or is_final)
        const isFinal = order.status?.is_final || false;
        if (statusName === 'Kết thúc' || statusName === 'Hoàn thành' || isFinal) {
          // Use completion_date if available, otherwise use updated_at
          let completedDate = order.completion_date ? new Date(order.completion_date) : null;
          if (!completedDate && order.updated_at) {
            completedDate = new Date(order.updated_at);
          }
          
          // Count if completed within the selected period
          if (!completedDate || (completedDate >= dateFrom && completedDate <= dateTo)) {
            completedOrders++;
          }
        }
      });
    }
    
    return {
      totalOrders,
      totalAmount,
      ordersByStatus,
      newOrders,
      completedOrders,
      period,
    };
  } catch (error) {
    throw error;
  }
};

// Get monthly statistics for last 6 months
export const getMonthlyStatistics = async (salesEmployeeId: string) => {
  try {
    const params = new URLSearchParams();
    params.append("sales_employee", salesEmployeeId);
    
    // Calculate date range for last 6 months
    const now = new Date();
    const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 5, 1); // Start from 6 months ago, first day
    
    params.append("sale_date__gte", sixMonthsAgo.toISOString().split('T')[0]);
    params.append("sale_date__lte", now.toISOString().split('T')[0]);
    
    const url = `/orders/?${params.toString()}`;
    const response = await axiosInstance.get(url);
    
    const orders = response.data.results || response.data;
    
    // Initialize data for last 6 months
    const monthlyData: Array<{
      month: string;
      monthIndex: number;
      year: number;
      newOrders: number;
      completedOrders: number;
    }> = [];
    
    // Generate last 6 months
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthIndex = date.getMonth();
      const year = date.getFullYear();
      const monthName = date.toLocaleDateString('vi-VN', { month: 'long', year: 'numeric' });
      
      monthlyData.push({
        month: monthName,
        monthIndex,
        year,
        newOrders: 0,
        completedOrders: 0,
      });
    }
    
    // Process orders
    if (Array.isArray(orders)) {
      orders.forEach((order: any) => {
        if (!order.sale_date) return;
        
        const orderDate = new Date(order.sale_date);
        
        
        
        // Check if order is new (created in this month)
        const createdDate = order.created_at ? new Date(order.created_at) : orderDate;
        const createdMonth = createdDate.getMonth();
        const createdYear = createdDate.getFullYear();
        
        // Find matching month for new orders
        const newOrderMonthData = monthlyData.find(
          (m) => m.monthIndex === createdMonth && m.year === createdYear
        );
        
        if (newOrderMonthData) {
          newOrderMonthData.newOrders++;
        }
        
        // Check if order is completed (status name is "Kết thúc" or is_final is true)
        const statusName = order.status?.name || order.status_name || '';
        const isFinal = order.status?.is_final || false;
        
        if (statusName === 'Kết thúc' || isFinal) {
          // Use completion_date if available, otherwise use sale_date
          let completedDate = orderDate;
          if (order.completion_date) {
            completedDate = new Date(order.completion_date);
          } else if (order.updated_at) {
            // If no completion_date, use updated_at as approximation
            completedDate = new Date(order.updated_at);
          }
          
          const completedMonth = completedDate.getMonth();
          const completedYear = completedDate.getFullYear();
          
          // Find matching month for completed orders
          const completedOrderMonthData = monthlyData.find(
            (m) => m.monthIndex === completedMonth && m.year === completedYear
          );
          
          if (completedOrderMonthData) {
            completedOrderMonthData.completedOrders++;
          }
        }
      });
    }
    
    return monthlyData;
  } catch (error) {
    throw error;
  }
};
